import React, { useState } from 'react';
import { downloadSingleChart } from '../utils/simpleChartDownload';

interface ChartDownloadButtonsProps {
  data: any;
}

const ChartDownloadButtons: React.FC<ChartDownloadButtonsProps> = ({ data }) => {
  const [generating, setGenerating] = useState<string | null>(null);

  const handleDownload = async (chartType: 'revenue' | 'growth' | 'impact' | 'metrics') => {
    setGenerating(chartType);
    try {
      await downloadSingleChart(data, chartType);
    } catch (error) {
      console.error('Chart download failed:', error);
      alert('Failed to download chart. Please try again.');
    } finally {
      setGenerating(null);
    }
  };

  const charts = [
    {
      id: 'revenue' as const,
      title: 'Revenue Metrics',
      description: 'MRR, ARR, and ARPU',
      icon: '💰',
      color: 'from-indigo-500 to-purple-600',
    },
    {
      id: 'growth' as const,
      title: 'User Growth',
      description: 'Total users & paying customers',
      icon: '📈',
      color: 'from-blue-500 to-cyan-600',
    },
    {
      id: 'impact' as const,
      title: 'Business Impact',
      description: 'Customer reach & engagement',
      icon: '🎯',
      color: 'from-green-500 to-emerald-600',
    },
    {
      id: 'metrics' as const,
      title: 'Key Metrics',
      description: 'Conversion, retention & growth',
      icon: '📊',
      color: 'from-pink-500 to-rose-600',
    },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            Download Investor Charts
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Professional charts for presentations and investor decks
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {charts.map((chart) => (
          <button
            key={chart.id}
            onClick={() => handleDownload(chart.id)}
            disabled={generating !== null}
            className={`
              relative overflow-hidden rounded-xl p-4 text-left transition-all duration-200
              ${generating === chart.id
                ? 'ring-2 ring-offset-2 ring-primary-500 dark:ring-offset-gray-800'
                : 'hover:shadow-lg hover:-translate-y-0.5'
              }
              disabled:opacity-50 disabled:cursor-not-allowed
              bg-gradient-to-br ${chart.color}
            `}
          >
            <div className="flex flex-col h-full">
              <div className="text-3xl mb-2">{chart.icon}</div>
              <div className="flex-1">
                <h4 className="text-base font-bold text-white mb-1">
                  {chart.title}
                </h4>
                <p className="text-xs text-white/80">
                  {chart.description}
                </p>
              </div>

              <div className="mt-3 flex items-center justify-between">
                {generating === chart.id ? (
                  <div className="flex items-center gap-2 text-white text-xs font-semibold">
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Generating...
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-white text-xs font-semibold">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download PNG
                  </div>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ChartDownloadButtons;
